import * as d3 from "d3";
import { colonAnnotation, createTooltip, numberFormat, responsivefy } from "../functions";

export default function (chart, divId, vizName, resData) {

    const innerWidth =
        chart.width - chart.margin.left - chart.margin.right;

    const innerHeight =
        chart.height - chart.margin.top - chart.margin.bottom;

    function renameKeys(obj) {
        obj.name = obj.key;
        delete obj.key;
        if (obj.values) {
            obj.children = obj.values;
            delete obj.values;
            obj.children.forEach(renameKeys);
        }
        return obj;
    }

    const data = d3.nest()
        .key(d => d.dim1_name)
        .key(d => d.dim2_name)
        .key(d => d.dim3_name)
        .rollup(v => d3.sum(v, d => d.metric1))
        .entries(resData);

    const Data = { key: chart.y1AxisDisplayName, values: data };
    renameKeys(Data);

    const root = d3.hierarchy(Data)
        .sum(d => d.value || 0)
        .sort((a, b) => b.value - a.value);

    const treemap = d3.treemap()
        .size([innerWidth, innerHeight])
        .paddingInner(2);

    const colorScale = d3.scaleLinear()
        .domain([0, root.value])        // root total
        .range(["#dbeafe", "#2563eb"]); // Tailwind blue-100 → blue-600

    let currentNode = root;


    treemap(root);

    d3.select(divId).select("svg").remove();

    const svg = d3.select(divId)
        .append("svg")
        .attr("width", chart.width)
        .attr("height", chart.height)
        .call(responsivefy)
        .append("g")
        .attr("transform", `translate(${chart.margin.left},${chart.margin.top})`);

    const grandparent = svg.append("g");

    grandparent.append("rect")
        .attr("y", -chart.margin.top)
        .attr("width", innerWidth)
        .attr("height", chart.margin.top)
        .attr("fill", "orange");

    const title = grandparent.append("text")
        .attr("x", 6)
        .attr("y", -chart.margin.top + 16)
        .attr("fill", "#000");

    const tooltip = createTooltip(divId);

    // let currentRoot = root;
    display(root);

    function truncate(text, width) {
        const maxChars = Math.floor(width / 7); // avg char width
        return text.length > maxChars
            ? text.slice(0, maxChars - 2) + ".."
            : text;
    }


    function display(d) {

        currentNode = d;

        treemap(root);

        const x = d3.scaleLinear()
            .domain([d.x0, d.x1])
            .range([0, innerWidth]);

        const y = d3.scaleLinear()
            .domain([d.y0, d.y1])
            .range([0, innerHeight]);
        title.text(
            `${breadcrumb(d)} (${numberFormat(d.value)})`
        );

        const g = svg.selectAll(".depth").data([d]);

        g.exit().remove();

        const depth = g.enter()
            .append("g")
            .attr("class", "depth")
            .merge(g);

        const nodes = depth.selectAll("g.node")
            .data(d.children || []);

        nodes.exit().remove();

        const nodeEnter = nodes.enter()
            .append("g")
            .attr("class", "node")
            .on("click", d => d.children && display(d));

        nodeEnter.append("rect");
        nodeEnter.append("text").attr("class", "label");

        const node = nodeEnter.merge(nodes);

        node.select("rect")
            .attr("x", d => x(d.x0))
            .attr("y", d => y(d.y0))
            .attr("width", d => x(d.x1) - x(d.x0))
            .attr("height", d => y(d.y1) - y(d.y0))
            .style("fill", d => colorScale(d.value))
            .on("mouseover", function (d) {
                tooltip.onMouseOver(d, this, () =>
                    `${d.data.name}${colonAnnotation} (${numberFormat(d.value)})`
                );
            })
            .on("mousemove", tooltip.onMouseMove)
            .on("mouseout", tooltip.onMouseLeave);

        node.select("text")
            .each(function (d) {

                const text = d3.select(this);
                text.selectAll("*").remove(); // clear old tspans

                const width = x(d.x1) - x(d.x0);
                const height = y(d.y1) - y(d.y0);

                // 🚫 Too small → no text
                if (width < 35 || height < 18) {
                    text.style("display", "none");
                    return;
                }

                text
                    .style("display", "block")
                    .attr("x", x(d.x0) + 6)
                    .attr("y", y(d.y0) + 16)
                    .style("font-weight", "600")
                    .style("font-size", width < 120 ? "10px" : "12px")
                    .style("fill", (() => {
                        const c = d3.hsl(colorScale(d.value));
                        return c.l < 0.5 ? "#ffffff" : "#000000";
                    })());

                const name = d.data.name;
                const value = numberFormat(d.value);

                // Line 1: name (truncate safely)
                text.append("tspan")
                    .attr("x", x(d.x0) + 6)
                    .attr("dy", 0)
                    .text(truncate(name, width));

                // Line 2: value (only if space allows)
                if (height > 40) {
                    text.append("tspan")
                        .attr("x", x(d.x0) + 6)
                        .attr("dy", "1.2em")
                        .style("font-weight", "400")
                        .text(`(${value})`);
                }
            });


        grandparent.on("click", function () {
            if (currentNode && currentNode.parent) {
                display(currentNode.parent);
            }
        });
    }

    function breadcrumb(d) {
        return d.parent
            ? breadcrumb(d.parent) + " / " + d.data.name
            : d.data.name;
    }
}
